import fs from 'fs';
import path from 'path';
import os from 'os';
import { logger } from '../utils/logger';
import { UserSchema, UsersDataSchema, type User, type UsersData } from '../types';

/**
 * UserManager class to handle CRUD operations on users with file persistence
 * and in-memory caching. Changes are synced to file periodically.
 */
export class UserManager {
  private users: User[] = [];
  private filePath: string;
  private hasChanged: boolean = false;
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private syncIntervalMs: number;
  private isInitialized: boolean = false;

  /**
   * Initialize UserManager with file path and sync interval
   * @param projectId - Project ID to use in file path (default: 'snowflake-dataset')
   * @param syncIntervalMs - Interval in milliseconds to sync changes to file (default: 5000ms)
   */
  constructor(projectId: string = 'snowflake-dataset', syncIntervalMs: number = 5000) {
    this.filePath = path.join(os.homedir(), '.superatom', 'projects', projectId, 'users.json');
    this.syncIntervalMs = syncIntervalMs;
  }

  /**
   * Initialize the UserManager by loading users from file and starting sync interval
   */
  async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Load users from file into memory
      await this.loadUsersFromFile();
      logger.info(`UserManager initialized with ${this.users.length} users`);

      // Start the sync interval
      this.startSyncInterval();
      this.isInitialized = true;
    } catch (error) {
      logger.error('Failed to initialize UserManager:', error);
      throw error;
    }
  }

  /**
   * Load users from the JSON file into memory
   */
  private async loadUsersFromFile(): Promise<void> {
    try {
      // Create directory structure if it doesn't exist
      const dir = path.dirname(this.filePath);
      if (!fs.existsSync(dir)) {
        logger.info(`Creating directory structure: ${dir}`);
        fs.mkdirSync(dir, { recursive: true });
      }

      // Create file with empty users array if it doesn't exist
      if (!fs.existsSync(this.filePath)) {
        logger.info(`Users file does not exist at ${this.filePath}, creating with empty users`);
        const initialData: UsersData = { users: [] };
        fs.writeFileSync(this.filePath, JSON.stringify(initialData, null, 4));
        this.users = [];
        this.hasChanged = false;
        return;
      }

      const fileContent = fs.readFileSync(this.filePath, 'utf-8');
      const rawData = JSON.parse(fileContent);

      // Validate using Zod schema
      const validatedData = UsersDataSchema.parse(rawData);
      this.users = validatedData.users;
      this.hasChanged = false;
      logger.debug(`Loaded ${this.users.length} users from file`);
    } catch (error) {
      logger.error('Failed to load users from file:', error);
      throw new Error(`Failed to load users from file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Save users from memory to the JSON file
   */
  private async saveUsersToFile(): Promise<void> {
    if (!this.hasChanged) {
      return;
    }

    try {
      // Create directory if it doesn't exist
      const dir = path.dirname(this.filePath);
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
      }

      // Filter out wsIds before saving to file (wsIds are only stored in memory)
      const usersToSave = this.users.map(user => {
        const { wsIds, ...userWithoutWsIds } = user;
        return userWithoutWsIds;
      });

      const data: UsersData = { users: usersToSave };
      fs.writeFileSync(this.filePath, JSON.stringify(data, null, 4));

      this.hasChanged = false;
      logger.debug(`Synced ${this.users.length} users to file (wsIds excluded)`);
    } catch (error) {
      logger.error('Failed to save users to file:', error);
      throw new Error(`Failed to save users to file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Start the periodic sync interval
   */
  private startSyncInterval(): void {
    if (this.syncInterval) {
      return;
    }

    this.syncInterval = setInterval(async () => {
      if (this.hasChanged) {
        try {
          await this.saveUsersToFile();
          logger.debug('Auto-sync: Users saved to file');
        } catch (error) {
          logger.error('Auto-sync failed:', error);
        }
      }
    }, this.syncIntervalMs);

    logger.debug(`Sync interval started (${this.syncIntervalMs}ms)`);
  }

  /**
   * Stop the periodic sync interval
   */
  public stopSyncInterval(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      logger.debug('Sync interval stopped');
    }
  }

  /**
   * Force sync users to file immediately
   */
  public async forceSync(): Promise<void> {
    await this.saveUsersToFile();
  }

  /**
   * Create a new user
   * @param user - User object to create
   * @returns The created user
   */
  public createUser(user: User): User {
    // Validate user data with Zod schema
    const validatedUser = UserSchema.parse(user);

    // Check for duplicate username
    if (this.users.some(u => u.username === validatedUser.username)) {
      throw new Error(`User with username ${validatedUser.username} already exists`);
    }

    // Check for duplicate email if email is provided
    if (validatedUser.email && this.users.some(u => u.email === validatedUser.email)) {
      throw new Error(`User with email ${validatedUser.email} already exists`);
    }

    this.users.push(validatedUser);
    this.hasChanged = true;
    logger.debug(`User created: ${validatedUser.username}`);

    return validatedUser;
  }

  /**
   * Read a user by username
   * @param username - Username to retrieve
   * @returns The user if found, undefined otherwise
   */
  public getUser(username: string): User | undefined {
    return this.users.find(u => u.username === username);
  }

  /**
   * Read a user by email
   * @param email - Email to retrieve
   * @returns The user if found, undefined otherwise
   */
  public getUserByEmail(email: string): User | undefined {
    return this.users.find(u => u.email === email);
  }

  /**
   * Find user by username or email
   * @param identifier - Username or email to search for
   * @returns The user if found, undefined otherwise
   */
  public getUserByUsernameOrEmail(identifier: string): User | undefined {
    return this.users.find(u => u.username === identifier || u.email === identifier);
  }

  /**
   * Read all users
   * @returns Array of all users
   */
  public getAllUsers(): User[] {
    return [...this.users];
  }

  /**
   * Find users by a predicate function
   * @param predicate - Function to filter users
   * @returns Array of matching users
   */
  public findUsers(predicate: (user: User) => boolean): User[] {
    return this.users.filter(predicate);
  }

  /**
   * Update an existing user by username
   * @param username - Username of user to update
   * @param updates - Partial user object with fields to update
   * @returns The updated user
   */
  public updateUser(username: string, updates: Partial<User>): User {
    const userIndex = this.users.findIndex(u => u.username === username);
    if (userIndex === -1) {
      throw new Error(`User with username ${username} not found`);
    }

    const updatedUser = { ...this.users[userIndex], ...updates };
    this.users[userIndex] = updatedUser;
    this.hasChanged = true;
    logger.debug(`User updated: ${username}`);

    return updatedUser;
  }

  /**
   * Delete a user by username
   * @param username - Username of user to delete
   * @returns true if user was deleted, false if not found
   */
  public deleteUser(username: string): boolean {
    const initialLength = this.users.length;
    this.users = this.users.filter(u => u.username !== username);

    if (this.users.length < initialLength) {
      this.hasChanged = true;
      logger.debug(`User deleted: ${username}`);
      return true;
    }
    return false;
  }

  /**
   * Delete all users
   */
  public deleteAllUsers(): void {
    if (this.users.length > 0) {
      this.users = [];
      this.hasChanged = true;
      logger.debug('All users deleted');
    }
  }

  /**
   * Get the count of users
   * @returns Number of users in memory
   */
  public getUserCount(): number {
    return this.users.length;
  }

  /**
   * Check if a user exists
   * @param username - Username to check
   * @returns true if user exists, false otherwise
   */
  public userExists(username: string): boolean {
    return this.users.some(u => u.username === username);
  }

  /**
   * Add a WebSocket ID to a user's wsIds array
   * @param username - Username to update
   * @param wsId - WebSocket ID to add
   * @returns true if successful, false if user not found
   */
  public addWsId(username: string, wsId: string): boolean {
    const user = this.getUser(username);
    if (!user) {
      return false;
    }

    if(!user.wsIds || !Array.isArray(user.wsIds)) {
      user.wsIds = [];
    }

    if (!user.wsIds.includes(wsId)) {
      user.wsIds.push(wsId);
      // this.hasChanged = true;
      logger.debug(`WebSocket ID added to user ${username}: ${wsId}`);
    }

    return true;
  }

  /**
   * Remove a WebSocket ID from a user's wsIds array
   * @param username - Username to update
   * @param wsId - WebSocket ID to remove
   * @returns true if successful, false if user not found
   */
  public removeWsId(username: string, wsId: string): boolean {
    const user = this.getUser(username);
    if (!user) {
      return false;
    }

    if(!user.wsIds || !Array.isArray(user.wsIds)) {
      return false;
    }

    const initialLength = user.wsIds.length;
    user.wsIds = user.wsIds.filter(id => id !== wsId);

    if (user.wsIds.length < initialLength) {
      // this.hasChanged = true;
      logger.debug(`WebSocket ID removed from user ${username}: ${wsId}`);
    }

    return true;
  }

  /**
   * Get the change status
   * @returns true if there are unsaved changes, false otherwise
   */
  public hasUnsavedChanges(): boolean {
    return this.hasChanged;
  }

  /**
   * Cleanup resources and stop sync interval
   */
  public async destroy(): Promise<void> {
    this.stopSyncInterval();
    // Final sync before cleanup
    if (this.hasChanged) {
      await this.saveUsersToFile();
    }
    logger.info('UserManager destroyed');
  }
}
